/** @jsxImportSource @emotion/react */
import CloseIcon from "@mui/icons-material/Close";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useState, VFC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { CountryPath } from "src/entities/CountryPath";
import getCountryName from "src/utils/getCountryName";
import Button from "../common/Button";

interface Props {
  open: boolean;
  onClose: () => void;
  articleId: string;
  currentCountry: CountryPath;
  setCountry: (country: CountryPath) => void;
}

const COUNTRIES: CountryPath[] = [
  "IDN",
  "SGP",
  "PHL",
  "MYS",
  "THA",
  "VNM",
  "AUS",
  "USA",
];

const DuplicateArticleModal: VFC<Props> = (props: Props) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState<CountryPath>(
    props.currentCountry
  );

  const handleDuplicate = () => {
    if (selectedCountry !== props.currentCountry) {
      props.setCountry(selectedCountry);
    }
    navigate(
      `/article/new/manual?country=${selectedCountry}&duplicateFrom=${props.articleId}`
    );
    props.onClose();
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      aria-labelledby="duplicate-article-dialog"
      maxWidth="xs"
      fullWidth
    >
      <div css={headerStyle}>
        <span css={titleStyle}>{t("duplicate_modal.title")}</span>
        <IconButton onClick={props.onClose} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent>
        <div css={descriptionStyle}>{t("duplicate_modal.description")}</div>
        <div css={warningStyle}>{t("duplicate_modal.language_warning")}</div>
        <RadioGroup
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value as CountryPath)}
        >
          {COUNTRIES.map((country) => (
            <FormControlLabel
              key={country}
              value={country}
              control={<Radio size="small" />}
              label={
                <span css={labelStyle}>
                  {getCountryName(country)}
                  {country === props.currentCountry && (
                    <span css={currentLabelStyle}>
                      {t("duplicate_modal.current_label")}
                    </span>
                  )}
                </span>
              }
              css={optionStyle}
            />
          ))}
        </RadioGroup>
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button
          variant="outlined"
          text={t("dashboard.modal.button.cancel")}
          onClick={props.onClose}
        />
        <Button text={t("duplicate_modal.confirm")} onClick={handleDuplicate} />
      </DialogActions>
    </Dialog>
  );
};

const headerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const titleStyle = css`
  font-size: 18px;
  font-weight: 600;
`;

const descriptionStyle = css`
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
`;

const optionStyle = css`
  margin: 0;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
  }
`;

const labelStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const currentLabelStyle = css`
  font-size: 12px;
  color: #0045ff;
  background: #e8f0ff;
  padding: 2px 8px;
  border-radius: 4px;
`;

const warningStyle = css`
  margin-bottom: 16px;
  padding: 12px;
  background: #fff8e6;
  border: 1px solid #ffcc00;
  border-radius: 4px;
  font-size: 13px;
  font-weight: bold;
  color: #664d00;
  line-height: 1.5;
`;

export default DuplicateArticleModal;
